/**
 * Order Bump Hook using TanStack Query
 * Replaces coordinator pattern with automatic cache invalidation
 */

import { useCallback, useEffect, useState } from 'react';

import { useTagadaContext } from '../providers/TagadaProvider';
import { getGlobalApiClient, useApiMutation, useInvalidateQuery } from './useApiQuery';
import { useCheckoutQuery } from './useCheckoutQuery';

export interface UseOrderBumpQueryOptions {
    checkoutToken: string | null;
    offerId: string;
}

export interface UseOrderBumpQueryResult {
    isSelected: boolean;
    isToggling: boolean;
    error: Error | null;
    toggle: (selected?: boolean) => Promise<{ success: boolean; error?: any }>;
}

export function useOrderBumpQuery(options: UseOrderBumpQueryOptions): UseOrderBumpQueryResult {
    const { checkoutToken, offerId } = options;
    const { invalidateCheckout, invalidatePromotions } = useInvalidateQuery();
    const { isSessionInitialized } = useTagadaContext();
    const client = getGlobalApiClient();

    const { checkout, isLoading: isCheckoutLoading, isSuccess: isCheckoutSuccess } = useCheckoutQuery({
        checkoutToken: checkoutToken || undefined,
        enabled: !!checkoutToken,
    });

    const actualCheckoutToken = checkoutToken || checkout?.checkoutSession?.checkoutToken || null;

    const checkOrderBumpSelection = useCallback(() => {
        if (!checkout?.checkoutSession?.sessionLineItems) {
            return false;
        }

        // Check if any order bump items exist in sessionLineItems
        // The offerId is the upsell offer ID, but we check for isOrderBump === true
        return checkout.checkoutSession.sessionLineItems.some((item: any) => {
            return item.isOrderBump === true;
        });
    }, [checkout?.checkoutSession?.sessionLineItems]);

    // State management
    const [isSelected, setIsSelected] = useState(() => checkOrderBumpSelection());
    const [error, setError] = useState<Error | null>(null);

    // Update state when checkout session changes
    useEffect(() => {
        const isSelectedFromSession = checkOrderBumpSelection();
        setIsSelected(isSelectedFromSession);
    }, [checkOrderBumpSelection]);


    // Order bump toggle mutation
    const toggleMutation = useApiMutation<
        { success: boolean; error?: any },
        { selected: boolean }
    >(
        async ({ selected }) => {
            if (!checkout?.checkoutSession?.id) {
                throw new Error('No checkout session available');
            }

            return client.post(`/api/v1/checkout-sessions/${checkout.checkoutSession.id}/toggle-order-bump`, {
                orderBumpOfferId: offerId,
                selected,
            });
        },
        {
            onMutate: ({ selected }) => {
                // Optimistic update
                setIsSelected(selected);
                setError(null);
            },
            onSuccess: () => {
                // Invalidate related queries
                // Use the actual checkout token (from parameter or checkout session)
                const tokenToUse = checkoutToken || checkout?.checkoutSession?.checkoutToken;
                if (tokenToUse) {
                    invalidateCheckout(String(tokenToUse));
                }
                // Always invalidate promotions based on checkout session ID
                if (checkout?.checkoutSession?.id) {
                    invalidatePromotions(String(checkout.checkoutSession.id));
                }
            },
            onError: (err, { selected }) => {
                // Revert optimistic update on error
                setIsSelected(!selected);
                setError(err instanceof Error ? err : new Error('Failed to toggle order bump'));
            },
        }
    );

    const toggle = useCallback(
        async (selected?: boolean) => {
            // Check if session is initialized (required for checkout query to run)
            if (!isSessionInitialized) {
                return { success: false, error: 'Session is not initialized yet. Please wait...' };
            }

            // Wait for checkout to finish loading if it's still loading
            if (isCheckoutLoading) {
                return { success: false, error: 'Checkout session is still loading. Please wait...' };
            }

            // Check if checkout failed to load (only if we have a token)
            if (checkoutToken && !isCheckoutSuccess && !checkout) {
                return { success: false, error: 'Failed to load checkout session. Please refresh and try again.' };
            }

            // Check if checkout session is available
            if (!checkout?.checkoutSession?.id) {
                return { success: false, error: 'Checkout session not ready' };
            }

            const targetState = selected ?? !isSelected;

            try {
                await toggleMutation.mutateAsync({ selected: targetState });
                return { success: true };
            } catch (err) {
                const error = err instanceof Error ? err : new Error('Failed to toggle order bump');
                return { success: false, error: error.message };
            }
        },
        [checkout?.checkoutSession?.id, isSelected, toggleMutation, offerId, checkoutToken, actualCheckoutToken, isCheckoutLoading, isCheckoutSuccess, checkout, isSessionInitialized]
    );

    return {
        isSelected,
        isToggling: toggleMutation.isPending,
        error,
        toggle,
    };
}
